import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { CAP } from '../utils/permissions';
import { usePermissions, ReadOnlyNotice } from '../utils/PermissionContext';
import { errorText } from '../utils/apiErrors';
import { apiFetch } from '../utils/apiAuth';
import { getVatDetails } from '../utils/vat';
import { useSortConfig } from '../hooks/useSortConfig';
import { useCloudRunJob } from '../hooks/useCloudRunJob';

const AdminGcp = ({ googleIdToken, t }) => {
  const { can } = usePermissions();
  const canWrite = can(CAP.ADMIN_WRITE);
  const { startJob } = useCloudRunJob();

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, handleSort] = useSortConfig({ key: 'billing_account_id', direction: 'asc' });
  const [selectedRecords, setSelectedRecords] = useState(new Set());
  const [sendingToAfas, setSendingToAfas] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [selectedDebtors, setSelectedDebtors] = useState(new Set());
  const [selectedInvoiceBy, setSelectedInvoiceBy] = useState(new Set());
  const [viewMode, setViewMode] = useState('ready_to_invoice'); // 'ready_to_invoice' | 'all' | 'unlinked' | 'internal'

  // Default to previous month YYYY-MM
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    now.setDate(1);
    now.setMonth(now.getMonth() - 1);
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const apiUrl = process.env.REACT_APP_API_URL || '';

  const fetchGcpData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch(`${apiUrl}/api/admin/gcp-billing?month=${selectedMonth}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorText(errorData, t, 'Failed to fetch GCP billing data'));
      }

      const data = await response.json();
      setRecords(data);
    } catch (err) {
      setError(err.message || 'Error loading GCP billing data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (googleIdToken) {
      fetchGcpData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, googleIdToken, selectedMonth]);

  useEffect(() => {
    setSelectedRecords(new Set());
  }, [viewMode, selectedMonth]);

  const counts = useMemo(() => {
    let ready = 0;
    let unlinked = 0;
    let internal = 0;
    records.forEach((r) => {
      const hasDebtor = Boolean(r.debtor_id && String(r.debtor_id).trim());
      const isInternal = String(r.debtor_id).trim() === '15802';
      const hasCost = Number(r.cost) !== 0;
      if (hasDebtor && !isInternal && hasCost) {
        ready++;
      }
      if (!hasDebtor) {
        unlinked++;
      }
      if (isInternal) {
        internal++;
      }
    });
    return {
      ready,
      unlinked,
      internal,
      all: records.length,
    };
  }, [records]);

  const uniqueDebtors = useMemo(() => {
    const debtors = new Set(
      records
        .map((r) => (r.debtor_id ? `${r.debtor_id} - ${r.debtor_name || ''}`.trim() : 'Unlinked'))
        .filter(Boolean)
    );
    return Array.from(debtors).sort();
  }, [records]);

  const uniqueInvoiceBy = useMemo(() => {
    const list = new Set(records.map((r) => r.invoice_by).filter(Boolean));
    return Array.from(list).sort();
  }, [records]);

  const filteredAndSortedRecords = useMemo(() => {
    return records
      .filter((rec) => {
        // View mode filter (Default: ready_to_invoice)
        if (viewMode === 'ready_to_invoice') {
          const hasDebtor = Boolean(rec.debtor_id && String(rec.debtor_id).trim());
          const isInternal = String(rec.debtor_id).trim() === '15802';
          const hasCost = Number(rec.cost) !== 0;
          if (!hasDebtor || isInternal || !hasCost) return false;
        } else if (viewMode === 'unlinked') {
          if (rec.debtor_id && String(rec.debtor_id).trim()) return false;
        } else if (viewMode === 'internal') {
          if (String(rec.debtor_id).trim() !== '15802') return false;
        }

        // Debtor filter
        if (selectedDebtors.size > 0) {
          const key = rec.debtor_id ? `${rec.debtor_id} - ${rec.debtor_name || ''}`.trim() : 'Unlinked';
          if (!selectedDebtors.has(key)) return false;
        }

        // Invoice By filter
        if (selectedInvoiceBy.size > 0) {
          if (!selectedInvoiceBy.has(rec.invoice_by)) return false;
        }

        // Search term
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
          (rec.billing_account_id && rec.billing_account_id.toLowerCase().includes(term)) ||
          (rec.customer_name && rec.customer_name.toLowerCase().includes(term)) ||
          (rec.project_id && rec.project_id.toLowerCase().includes(term)) ||
          (rec.project_name && rec.project_name.toLowerCase().includes(term)) ||
          (rec.debtor_id && rec.debtor_id.toLowerCase().includes(term)) ||
          (rec.debtor_name && rec.debtor_name.toLowerCase().includes(term)) ||
          (rec.po_number && rec.po_number.toLowerCase().includes(term)) ||
          (rec.created_order_id && rec.created_order_id.toLowerCase().includes(term)) ||
          (rec.text && rec.text.toLowerCase().includes(term)) ||
          (rec.invoice_by && rec.invoice_by.toLowerCase().includes(term))
        );
      })
      .sort((a, b) => {
        let valA = a[sortConfig.key];
        let valB = b[sortConfig.key];

        if (typeof valA === 'number' && typeof valB === 'number') {
          return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
        }

        valA = (valA || '').toString().toLowerCase();
        valB = (valB || '').toString().toLowerCase();

        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
  }, [records, searchTerm, selectedDebtors, selectedInvoiceBy, sortConfig, viewMode]);

  const recordId = (r) => `${r.billing_account_id}-${r.project_id}`;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = filteredAndSortedRecords.map(recordId);
      setSelectedRecords(new Set(allIds));
    } else {
      setSelectedRecords(new Set());
    }
  };

  const handleSelectRecord = (id) => {
    const newSelected = new Set(selectedRecords);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRecords(newSelected);
  };

  const handleSelectGroup = (billingAccountId, checked) => {
    const newSelected = new Set(selectedRecords);
    filteredAndSortedRecords.forEach((r) => {
      if (r.billing_account_id === billingAccountId) {
        const id = recordId(r);
        if (checked) {
          newSelected.add(id);
        } else {
          newSelected.delete(id);
        }
      }
    });
    setSelectedRecords(newSelected);
  };

  const isGroupFullySelected = (billingAccountId) => {
    const groupRecords = filteredAndSortedRecords.filter((r) => r.billing_account_id === billingAccountId);
    if (groupRecords.length === 0) return false;
    return groupRecords.every((r) => selectedRecords.has(recordId(r)));
  };

  const handleSendToAfas = () => {
    setShowConfirmModal(true);
  };

  const executeSendToAfas = async () => {
    const recordsToSend = selectedRecords.size === 0
      ? filteredAndSortedRecords
      : filteredAndSortedRecords.filter((r) => selectedRecords.has(recordId(r)));

    setShowConfirmModal(false);
    setSendingToAfas(true);
    setSuccessMessage('');

    try {
      await startJob(
        '/api/jobs/afas-gcp-invoices',
        { month: selectedMonth, records: recordsToSend },
        { t, onFinished: () => fetchGcpData() }
      );
      setSuccessMessage(`Job triggered successfully for ${recordsToSend.length} GCP records.`);
      setSelectedRecords(new Set());
    } catch (err) {
      // Not setError: that swaps the whole screen for the load-error view.
      toast.error(err.message);
    } finally {
      setSendingToAfas(false);
    }
  };

  const toggleDebtor = (debtor) => {
    const next = new Set(selectedDebtors);
    if (next.has(debtor)) next.delete(debtor);
    else next.add(debtor);
    setSelectedDebtors(next);
  };

  const toggleInvoiceBy = (inv) => {
    const next = new Set(selectedInvoiceBy);
    if (next.has(inv)) next.delete(inv);
    else next.add(inv);
    setSelectedInvoiceBy(next);
  };

  if (loading) {
    return (
      <div className="p-6 animate-pulse">
        <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
          <div>
            <div className="h-8 bg-gray-200 rounded w-64 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-96"></div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="h-10 bg-gray-200 rounded w-32"></div>
            <div className="h-10 bg-gray-200 rounded w-32"></div>
            <div className="h-10 bg-gray-200 rounded w-64"></div>
          </div>
        </div>
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-primary opacity-85">
              <tr>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                  <th key={i} className="px-6 py-4">
                    <div className="h-3 bg-white/40 rounded w-full"></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {[1, 2, 3, 4, 5].map((rowIdx) => (
                <tr key={rowIdx}>
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-4"></div></td>
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16"></div></td>
                  <td className="px-6 py-4">
                    <div className="h-4 bg-gray-200 rounded w-48 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                  </td>
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-32"></div></td>
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-16 ml-auto"></div></td>
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-20 ml-auto"></div></td>
                  <td className="px-6 py-4"><div className="h-4 bg-gray-200 rounded w-24 ml-auto"></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-md m-6">
        <h3 className="text-red-800 font-medium">Error Loading Data</h3>
        <p className="text-red-600 mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <ReadOnlyNotice t={t} />

      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div className="flex items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-8 w-8 mr-3 bg-primary text-white rounded-full p-1.5 flex-shrink-0 shadow-sm"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 00-9.78 2.096A4.001 4.001 0 003 15z"
            />
          </svg>
          <h2 className="text-2xl font-bold text-gray-900">GCP</h2>
        </div>

        <div className="flex items-center space-x-4">
          <input
            type="month"
            className="border border-gray-300 rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary text-gray-700"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          />

          {canWrite && (
            <button
              onClick={handleSendToAfas}
              disabled={sendingToAfas || filteredAndSortedRecords.length === 0}
              className={`px-4 py-2 rounded-md text-sm font-medium text-white transition-colors ${
                sendingToAfas || filteredAndSortedRecords.length === 0
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-primary hover:bg-opacity-90 shadow-sm'
              }`}
            >
              {sendingToAfas ? 'Sending...' : 'Send to AFAS'}
            </button>
          )}

          <button
            onClick={() => setShowFiltersModal(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Filters
            {(selectedDebtors.size > 0 || selectedInvoiceBy.size > 0) && (
              <span className="bg-primary text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center ml-1">
                {selectedDebtors.size + selectedInvoiceBy.size}
              </span>
            )}
          </button>

          <input
            type="text"
            placeholder="Search billing account, project, debtor..."
            className="border border-gray-300 rounded-md px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary w-64"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          <div className="bg-indigo-50 text-indigo-700 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap">
            Showing: {filteredAndSortedRecords.length}
          </div>
        </div>
      </div>

      {/* View Mode Filter Tabs */}
      <div className="mb-4 flex flex-wrap items-center justify-end gap-1.5">
        <button
          type="button"
          onClick={() => setViewMode('ready_to_invoice')}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
            viewMode === 'ready_to_invoice'
              ? 'bg-primary text-white shadow-sm'
              : 'bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 border border-gray-200'
          }`}
        >
          <span>Ready to Invoice</span>
          <span
            className={`text-[10px] px-1.5 py-0.2 rounded-full font-semibold ${
              viewMode === 'ready_to_invoice'
                ? 'bg-white/20 text-white'
                : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
            }`}
          >
            {counts.ready}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setViewMode('all')}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
            viewMode === 'all'
              ? 'bg-primary text-white shadow-sm'
              : 'bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 border border-gray-200'
          }`}
        >
          <span>All Records</span>
          <span
            className={`text-[10px] px-1.5 py-0.2 rounded-full font-semibold ${
              viewMode === 'all'
                ? 'bg-white/20 text-white'
                : 'bg-gray-100 text-gray-600'
            }`}
          >
            {counts.all}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setViewMode('unlinked')}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
            viewMode === 'unlinked'
              ? 'bg-primary text-white shadow-sm'
              : 'bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 border border-gray-200'
          }`}
        >
          <span>Unlinked Accounts</span>
          <span
            className={`text-[10px] px-1.5 py-0.2 rounded-full font-semibold ${
              viewMode === 'unlinked'
                ? 'bg-white/20 text-white'
                : 'bg-amber-50 text-amber-700 border border-amber-200'
            }`}
          >
            {counts.unlinked}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setViewMode('internal')}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
            viewMode === 'internal'
              ? 'bg-primary text-white shadow-sm'
              : 'bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 border border-gray-200'
          }`}
        >
          <span>Internal (15802)</span>
          <span
            className={`text-[10px] px-1.5 py-0.2 rounded-full font-semibold ${
              viewMode === 'internal'
                ? 'bg-white/20 text-white'
                : 'bg-blue-50 text-blue-700 border border-blue-200'
            }`}
          >
            {counts.internal}
          </span>
        </button>
      </div>

      {successMessage && (
        <div className="mb-4 bg-green-50 p-4 rounded-md">
          <p className="text-green-800 text-sm font-medium">{successMessage}</p>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-primary text-white">
            <tr>
              <th scope="col" className="px-6 py-3 text-left">
                {canWrite && (
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    onChange={handleSelectAll}
                    checked={
                      filteredAndSortedRecords.length > 0 &&
                      selectedRecords.size === filteredAndSortedRecords.length
                    }
                  />
                )}
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90"
                onClick={() => handleSort('billing_account_id')}
              >
                Billing Account {sortConfig.key === 'billing_account_id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90"
                onClick={() => handleSort('debtor_id')}
              >
                Debtor ID {sortConfig.key === 'debtor_id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90"
                onClick={() => handleSort('project_id')}
              >
                Project {sortConfig.key === 'project_id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90"
                onClick={() => handleSort('text')}
              >
                Description {sortConfig.key === 'text' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90"
                onClick={() => handleSort('po_number')}
              >
                PO Number {sortConfig.key === 'po_number' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90"
                onClick={() => handleSort('cost')}
              >
                Cost {sortConfig.key === 'cost' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider cursor-pointer hover:bg-opacity-90"
                onClick={() => handleSort('created_order_id')}
              >
                Created Order ID {sortConfig.key === 'created_order_id' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredAndSortedRecords.map((r, index) => {
              const id = recordId(r);
              const showGroupHeader =
                sortConfig.key === 'billing_account_id' &&
                (index === 0 || r.billing_account_id !== filteredAndSortedRecords[index - 1].billing_account_id);
              const isLastInGroup =
                sortConfig.key === 'billing_account_id' &&
                (index === filteredAndSortedRecords.length - 1 ||
                  r.billing_account_id !== filteredAndSortedRecords[index + 1].billing_account_id);

              let subtotal = 0, vatPercentage = 0, vatAmount = 0, grandTotal = 0, vatLabel = '0%';
              if (isLastInGroup) {
                const groupRecords = filteredAndSortedRecords.filter(
                  (item) => item.billing_account_id === r.billing_account_id
                );
                subtotal = groupRecords.reduce((sum, item) => sum + (item.cost || 0), 0);

                const vatDetails = getVatDetails({
                  administratie: r.invoice_by,
                  country: r.country,
                  vatCode: r.vatCode,
                });
                vatPercentage = vatDetails.vatRate;
                vatAmount = subtotal * vatPercentage;
                grandTotal = subtotal + vatAmount;
                vatLabel = vatDetails.vatCode ? `(${vatDetails.vatCode}, ${vatDetails.vatPercent}%)` : `(${vatDetails.vatPercent}%)`;
              }

              return (
                <React.Fragment key={id}>
                  {showGroupHeader && (
                    <tr className="bg-gray-100 border-y border-gray-200">
                      <td className="px-6 py-2">
                        {canWrite && (
                          <input
                            type="checkbox"
                            className="rounded border-gray-400 text-primary shadow-sm focus:border-primary focus:ring focus:ring-primary focus:ring-opacity-50"
                            checked={isGroupFullySelected(r.billing_account_id)}
                            onChange={(e) => handleSelectGroup(r.billing_account_id, e.target.checked)}
                          />
                        )}
                      </td>
                      <td colSpan="7" className="px-6 py-2 text-sm font-bold text-gray-800">
                        Select all "{r.debtor_name ? `${r.debtor_name} (${r.billing_account_id})` : (r.customer_name ? `${r.customer_name} (${r.billing_account_id})` : r.billing_account_id)}"
                      </td>
                    </tr>
                  )}

                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {canWrite && (
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                          checked={selectedRecords.has(id)}
                          onChange={() => handleSelectRecord(id)}
                        />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="font-mono font-medium text-gray-900">{r.billing_account_id}</div>
                      {(r.customer_name || r.debtor_name) && (
                        <div
                          className="text-xs text-gray-500 font-sans mt-0.5 truncate max-w-[200px]"
                          title={r.customer_name || r.debtor_name}
                        >
                          {r.customer_name || r.debtor_name}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {r.debtor_id ? (
                        <div className="flex flex-col items-start">
                          <Link
                            to={`/admin/customers/${r.debtor_id}`}
                            className="inline-block px-1.5 py-0.5 rounded text-xs font-bold text-purple-700 bg-purple-50 hover:bg-purple-100 hover:text-purple-900 hover:underline border border-purple-200 transition-colors cursor-pointer"
                            title={r.debtor_name || r.debtor_id}
                          >
                            {r.debtor_id}
                          </Link>
                          {r.debtor_name && (
                            <Link
                              to={`/admin/customers/${r.debtor_id}`}
                              className="block text-xs text-gray-500 hover:text-gray-800 hover:underline truncate max-w-[180px] mt-0.5 cursor-pointer"
                              title={r.debtor_name}
                            >
                              {r.debtor_name}
                            </Link>
                          )}
                        </div>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                          Unlinked
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="font-medium">{r.project_id}</div>
                      {r.project_name && r.project_name !== r.project_id && r.project_name !== '[Charges not specific to a project]' && (
                        <div className="text-xs text-gray-500">{r.project_name}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-normal text-sm text-gray-600 min-w-[240px]">
                      {r.text}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500">
                      {r.po_number || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                      {r.cost ? r.cost.toFixed(3) : '0.000'} {r.currency || 'EUR'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {r.created_order_id ? (
                        <div className="flex items-center justify-end gap-1.5">
                          {r.isCreditNote && (
                            <span 
                              className="inline-flex items-center justify-center w-4 h-4 bg-red-500 text-white rounded-full font-bold text-[10px] leading-none select-none"
                              title="Credit Note"
                            >
                              -
                            </span>
                          )}
                          <Link
                            to={`/admin/afas-transactions/${r.created_order_id}`}
                            className="text-indigo-600 font-semibold hover:text-indigo-800 hover:underline transition-colors"
                          >
                            {r.created_order_id}
                          </Link>
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>

                  {isLastInGroup && (
                    <>
                      <tr className="bg-gray-50 border-t border-gray-200">
                        <td colSpan="6" className="px-6 py-2 text-right text-sm font-bold text-gray-500">
                          Subtotal
                        </td>
                        <td className="px-6 py-2 text-right text-sm font-bold text-gray-700">
                          {subtotal.toFixed(2)} EUR
                        </td>
                        <td></td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td colSpan="6" className="px-6 py-2 text-right text-sm font-bold text-gray-500">
                          VAT {vatLabel}
                        </td>
                        <td className="px-6 py-2 text-right text-sm font-bold text-gray-700">
                          {vatAmount.toFixed(2)} EUR
                        </td>
                        <td></td>
                      </tr>
                      <tr className="bg-blue-50/50 border-b-2 border-gray-300">
                        <td colSpan="6" className="px-6 py-3 text-right text-sm font-black text-gray-800 uppercase tracking-wider">
                          Total Invoice Amount
                        </td>
                        <td className="px-6 py-3 text-right text-sm font-black text-indigo-700">
                          {grandTotal.toFixed(2)} EUR
                        </td>
                        <td></td>
                      </tr>
                    </>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {filteredAndSortedRecords.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            {viewMode === 'ready_to_invoice'
              ? `No billable GCP records found for ${selectedMonth}.`
              : viewMode === 'unlinked'
              ? `No unlinked GCP accounts found for ${selectedMonth}.`
              : viewMode === 'internal'
              ? `No internal (15802) records found for ${selectedMonth}.`
              : `No GCP billing data found for ${selectedMonth}.`}
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900 bg-opacity-50 transition-opacity">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 animate-in fade-in zoom-in-95 duration-200">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Process GCP Records?</h3>
            <p className="text-gray-600 mb-6 text-sm">
              {selectedRecords.size === 0
                ? `No records selected. Are you sure you want to process all ${filteredAndSortedRecords.length} records?`
                : `Are you sure you want to process ${selectedRecords.size} selected record(s)?`}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={executeSendToAfas}
                className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary shadow-sm transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters Modal */}
      {showFiltersModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900 bg-opacity-50 transition-opacity">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl p-6 max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Filter GCP Records</h3>
              <button onClick={() => setShowFiltersModal(false)} className="text-gray-400 hover:text-gray-500 focus:outline-none">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="overflow-y-auto flex-1 pr-2 space-y-6">
              {uniqueDebtors.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-sm font-semibold text-gray-700">Debtor:</span>
                    {selectedDebtors.size > 0 && (
                      <button
                        onClick={() => setSelectedDebtors(new Set())}
                        className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-1 rounded-full transition-colors focus:outline-none"
                        title="Clear Debtor Filters"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {uniqueDebtors.map((debtor) => {
                      const isSelected = selectedDebtors.has(debtor);
                      return (
                        <button
                          key={debtor}
                          onClick={() => toggleDebtor(debtor)}
                          className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all duration-200 border select-none ${
                            isSelected
                              ? 'bg-primary text-white border-primary shadow-sm shadow-primary/20 scale-105'
                              : 'bg-white text-primary border-primary/20 hover:bg-primary/5 hover:border-primary/40'
                          }`}
                        >
                          {debtor}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {uniqueInvoiceBy.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-sm font-semibold text-gray-700">Invoice By (Administration):</span>
                    {selectedInvoiceBy.size > 0 && (
                      <button
                        onClick={() => setSelectedInvoiceBy(new Set())}
                        className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-1 rounded-full transition-colors focus:outline-none"
                        title="Clear Invoice By Filters"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {uniqueInvoiceBy.map((inv) => {
                      const isSelected = selectedInvoiceBy.has(inv);
                      return (
                        <button
                          key={inv}
                          onClick={() => toggleInvoiceBy(inv)}
                          className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-all duration-200 border select-none ${
                            isSelected
                              ? 'bg-teal-600 text-white border-teal-600 shadow-sm shadow-teal-600/20 scale-105'
                              : 'bg-white text-teal-700 border-teal-200 hover:bg-teal-50/50 hover:border-teal-300'
                          }`}
                        >
                          {inv}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-gray-200">
              {(selectedDebtors.size > 0 || selectedInvoiceBy.size > 0) && (
                <button
                  onClick={() => {
                    setSelectedDebtors(new Set());
                    setSelectedInvoiceBy(new Set());
                  }}
                  className="px-4 py-2 text-sm font-medium text-red-700 bg-white border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                >
                  Clear All
                </button>
              )}
              <button
                onClick={() => setShowFiltersModal(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-opacity-90 transition-colors shadow-sm"
              >
                Close & Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGcp;
